import { ControlledToggleButtonGroup, UncontrolledTextFieldDebounced } from '@if/mui'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Typography,
} from '@mui/material'
import { JSX } from 'react'
import { Control } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

interface ManualLinkFormValues {
  applicationNumber: string
  numberType: 'dcc' | 'cvt'
  financingType: 'fa' | 'ifx' | 'if'
}

interface UnlinkConfirmDialogProps {
  open: boolean
  onCancel: () => void
  onConfirm: () => void
  isUnlinking: boolean
  isReplacing: boolean
  wantsReplace: boolean
  onWantsReplaceChange: (checked: boolean) => void
  replaceFormControl: Control<ManualLinkFormValues>
  replaceNumberType: 'dcc' | 'cvt'
  replaceApplicationNumber: string
  numberTypeOptions: JSX.Element[]
}

const UnlinkConfirmDialog = ({
  open,
  onCancel,
  onConfirm,
  isUnlinking,
  isReplacing,
  wantsReplace,
  onWantsReplaceChange,
  replaceFormControl,
  replaceNumberType,
  replaceApplicationNumber,
  numberTypeOptions,
}: UnlinkConfirmDialogProps) => {
  const { t } = useTranslation()

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm">
      <DialogTitle>{t('docsAutomation.unlink')}</DialogTitle>
      <DialogContent>
        <DialogContentText>{t('docsAutomation.unlinkConfirmMessage')}</DialogContentText>

        <Box
          sx={{
            mt: 2,
            p: 2,
            border: 1,
            borderColor: 'warning.main',
            borderRadius: 1,
            bgcolor: 'rgba(255, 152, 0, 0.1)',
            display: 'flex',
            gap: 1,
            alignItems: 'flex-start',
          }}
        >
          <WarningAmberIcon color="warning" />
          <Typography variant="body2" color="text.primary">
            {t('docsAutomation.unlinkWarning')}
          </Typography>
        </Box>

        <Box
          sx={{
            mt: 2,
            p: 2,
            border: 1,
            borderColor: 'info.main',
            borderRadius: 1,
            bgcolor: 'rgba(33, 150, 243, 0.1)',
          }}
        >
          <Typography variant="body2" color="text.primary">
            {t('docsAutomation.unlinkInfo')}
          </Typography>
        </Box>

        <Box sx={{ mt: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={wantsReplace}
                onChange={(e) => onWantsReplaceChange(e.target.checked)}
                disabled={isUnlinking}
              />
            }
            label={t('docsAutomation.replaceLink')}
          />
        </Box>

        {wantsReplace && (
          <Box sx={{ mt: 2, p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'end' }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {t('common.applicationReferenceNumber')}
                </Typography>
                <UncontrolledTextFieldDebounced
                  control={replaceFormControl}
                  name="applicationNumber"
                  label={replaceNumberType === 'dcc' ? t('common.dccNumber') : t('common.cvtNumber')}
                  type="number"
                  fullWidth={false}
                  size="small"
                  sx={{ width: '100%' }}
                />
              </Box>

              <ControlledToggleButtonGroup
                control={replaceFormControl}
                name="numberType"
                exclusive
                color="primary"
                size="small"
                options={numberTypeOptions}
              />
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={isUnlinking}>
          {t('common.cancel')}
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          color={wantsReplace ? 'primary' : 'error'}
          loading={isUnlinking || isReplacing}
          disabled={(wantsReplace && !replaceApplicationNumber) || isUnlinking || isReplacing}
        >
          {wantsReplace ? t('docsAutomation.replaceAndSave') : t('common.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default UnlinkConfirmDialog
